import { HTTPError } from 'ky';

import { placesKyClient } from './placesKyClient';

/** Places API (New) REST text match offsets — mirrors FormattableText matches JSON. */
export type PlaceTextMatch = {
  startOffset?: number;
  endOffset: number;
};

/** App DTO mapped from Places Autocomplete (New) suggestions. */
export type PlacePrediction = {
  placeId: string;
  description: string;
  matches: PlaceTextMatch[];
};

/** Places API (New) address component JSON (field names align with google.maps.places.AddressComponent). */
type PlaceAddressComponent = {
  longText: string;
  shortText: string;
  types: string[];
  languageCode?: string;
};

export type PlaceDetails = {
  addressComponents?: PlaceAddressComponent[];
};

type AutocompleteRequest = {
  input: string;
  includedRegionCodes?: string[];
  includedPrimaryTypes?: string[];
  sessionToken?: string;
};

type PlacesAutocompleteResponse = {
  suggestions?: Array<{
    placePrediction?: {
      placeId: string;
      text?: {
        text: string;
        matches?: PlaceTextMatch[];
      };
    };
  }>;
};

/** Matches legacy `types: ['address']` — street_address alone returns no suggestions for partial input (e.g. "Pitt"). */
export const AU_ADDRESS_PRIMARY_TYPES = ['route', 'premise', 'subpremise', 'street_address'] as const;

export const AU_ADDRESS_AUTOCOMPLETE_DEFAULTS = {
  includedRegionCodes: ['au'],
  includedPrimaryTypes: AU_ADDRESS_PRIMARY_TYPES,
  regionCode: 'AU',
} as const;

type PlacesRequestOptions = {
  method: 'GET' | 'POST';
  fieldMask: string;
  body?: object;
  searchParams?: Record<string, string>;
};

const placesRequest = async <T>(path: string, options: PlacesRequestOptions): Promise<T> => {
  if (!process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY) {
    throw new Error('Places API key is not configured');
  }

  const headers = {
    'X-Goog-FieldMask': options.fieldMask,
  };

  try {
    if (options.method === 'POST') {
      return await placesKyClient
        .post(path, {
          json: options.body,
          headers,
        })
        .json<T>();
    }

    return await placesKyClient
      .get(path, {
        searchParams: options.searchParams,
        headers,
      })
      .json<T>();
  } catch (error: unknown) {
    const status =
      error instanceof HTTPError
        ? error.response.status
        : (error as { response?: Response }).response?.status;

    if (status !== undefined) {
      throw new Error(`Places API returned ${status}`);
    }

    throw new Error('Places API request failed');
  }
};

const mapSuggestionsToPredictions = (response: PlacesAutocompleteResponse): PlacePrediction[] => {
  if (!response.suggestions) return [];

  return response.suggestions
    .filter((suggestion) => suggestion.placePrediction)
    .map((suggestion) => {
      const prediction = suggestion.placePrediction!;
      return {
        placeId: prediction.placeId,
        description: prediction.text?.text ?? '',
        matches: prediction.text?.matches ?? [],
      };
    });
};

const buildAutocompleteBody = (request: AutocompleteRequest) => {
  const body: {
    input: string;
    includedRegionCodes: string[];
    includedPrimaryTypes: string[];
    regionCode: string;
    sessionToken?: string;
  } = {
    input: request.input.trim(),
    includedRegionCodes: request.includedRegionCodes ?? [...AU_ADDRESS_AUTOCOMPLETE_DEFAULTS.includedRegionCodes],
    includedPrimaryTypes: request.includedPrimaryTypes ?? [...AU_ADDRESS_AUTOCOMPLETE_DEFAULTS.includedPrimaryTypes],
    regionCode: AU_ADDRESS_AUTOCOMPLETE_DEFAULTS.regionCode,
  };

  if (request.sessionToken) {
    body.sessionToken = request.sessionToken;
  }

  return body;
};

export const autocompletePlaces = async (request: AutocompleteRequest): Promise<PlacePrediction[]> => {
  const input = request.input?.trim() ?? '';

  if (!input) return [];

  const data = await placesRequest<PlacesAutocompleteResponse>('places:autocomplete', {
    method: 'POST',
    fieldMask: 'suggestions.placePrediction.placeId,suggestions.placePrediction.text',
    body: buildAutocompleteBody({ ...request, input }),
  });

  return mapSuggestionsToPredictions(data);
};

export const getPlaceDetails = async (placeId: string, sessionToken?: string): Promise<PlaceDetails> => {
  if (!placeId) {
    throw new Error('Place ID is required');
  }

  const searchParams = sessionToken ? { sessionToken } : undefined;

  return placesRequest<PlaceDetails>(`places/${placeId}`, {
    method: 'GET',
    fieldMask: 'addressComponents',
    searchParams,
  });
};
